// src/pages/forge/SquadMembersPage.tsx - COMPLETE FIXED VERSION WITH SCROLLING DIALOGS
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Users,
  CalendarPlus,
  Plus,
  FileText,
  Download,
  Upload,
  Eye,
  MessageSquare,
  Video,
  Calendar,
  Link as LinkIcon,
  Send,
  MoreVertical,
  CheckCircle2,
  Save,
  Edit,
  Trash2,
  MessageCircle,
  RefreshCw,
  FolderOpen,
  Search,
  Filter,
  X,
  Home
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { supabase } from '@/lib/supabaseClient';

// ========== HELPER FUNCTIONS ==========
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleBadgeColor = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'market_analyst': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'product_lead': return 'bg-green-100 text-green-800 border-green-200';
    case 'finance_lead': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getDisplayRole = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'Project Lead';
    case 'market_analyst': return 'Market Analyst';
    case 'product_lead': return 'Product Lead';
    case 'finance_lead': return 'Finance Lead';
    default: return role || 'Team Member';
  }
};

const getPriorityColor = (priority: string) => {
  switch(priority) {
    case 'high': return 'bg-red-100 text-red-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusBadge = (status: string) => {
  switch(status) {
    case 'approved': return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
    case 'reviewing': return <Badge className="bg-yellow-100 text-yellow-700">Reviewing</Badge>;
    case 'pending': return <Badge className="bg-orange-100 text-orange-700">Pending</Badge>;
    case 'rejected': return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    case 'completed': return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    case 'in_progress': return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
};

// ========== TYPES ==========
interface Document {
  id: string;
  name: string;
  description: string;
  category: string;
  file_name: string;
  file_type: string;
  file_size: string;
  file_url: string;
  status: string;
  uploaded_by_name: string;
  created_at: string;
}

interface DocumentComment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  due_date: string;
  priority: string;
  status: string;
  created_by_name: string;
  created_at: string;
}

interface Meeting {
  id: string;
  title: string;
  description: string;
  meeting_date: string;
  meeting_time: string;
  type: string;
  agenda: string;
  meeting_link: string;
  status: string;
  created_by_name: string;
  attendees?: MeetingAttendee[];
}

interface MeetingAttendee {
  id: string;
  attendee_name: string;
  attendee_role: string;
  attendee_type: string;
  rsvp_status: string;
}

interface DocumentFilters {
  search: string;
  category: string;
  status: string;
}

const ANALYST_OPTIONS = [
  { id: 'analyst1', name: 'Dr. Sarah Kimani', role: 'Senior Analyst', type: 'analyst' },
  { id: 'analyst2', name: 'James Mwangi', role: 'Problem Bank Analyst', type: 'analyst' },
  { id: 'analyst3', name: 'Wanjiku Ndegwa', role: 'Business Analyst', type: 'analyst' },
  { id: 'analyst4', name: 'Michael Otieno', role: 'Market Analyst', type: 'analyst' },
  { id: 'analyst5', name: 'Grace Achieng', role: 'Financial Analyst', type: 'analyst' },
];

export default function SquadMembersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { squadUser } = useSquadAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  const [members, setMembers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [activeDocumentMember, setActiveDocumentMember] = useState<any>(null);
  const [memberKPIProgress, setMemberKPIProgress] = useState<Record<string, { completed: number; total: number }>>({});
  
  const [documents, setDocuments] = useState<Record<string, Document[]>>({});
  const [tasks, setTasks] = useState<Record<string, Task[]>>({});
  const [meetings, setMeetings] = useState<Record<string, Meeting[]>>({});
  const [comments, setComments] = useState<Record<string, DocumentComment[]>>({});
  
  const [docFilters, setDocFilters] = useState<DocumentFilters>({
    search: '',
    category: 'all',
    status: 'all'
  });
  
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showDocumentUploadDialog, setShowDocumentUploadDialog] = useState(false);
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [showDocumentsTabDialog, setShowDocumentsTabDialog] = useState(false);
  const [showDocumentCommentsDialog, setShowDocumentCommentsDialog] = useState(false);
  const [showAddCommentDialog, setShowAddCommentDialog] = useState(false);
  
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentAttachment, setCommentAttachment] = useState<File | null>(null);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium'
  });
  
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    meeting_date: '',
    meeting_time: '',
    type: 'internal',
    agenda: '',
    meeting_link: '',
    attendees: [] as string[]
  });
  
  const [messageContent, setMessageContent] = useState('');
  
  const [newDocument, setNewDocument] = useState({
    name: '',
    description: '',
    category: 'other' as 'proposal' | 'research' | 'financial' | 'legal' | 'other',
    file: null as File | null
  });
  
  const [editedBio, setEditedBio] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedLocation, setEditedLocation] = useState('');

  useEffect(() => {
    if (squadUser?.squadId) {
      loadAllData();
    }
  }, [squadUser]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [membersResult, documentsResult, tasksResult, meetingsResult] = await Promise.all([
        supabase.from('squad_members').select('*').eq('squad_id', squadUser?.squadId),
        supabase.from('squad_documents').select('*').order('created_at', { ascending: false }),
        supabase.from('squad_tasks').select('*').order('due_date', { ascending: true }),
        supabase.from('squad_meetings').select('*').order('meeting_date', { ascending: true })
      ]);

      if (membersResult.error) throw membersResult.error;
      if (membersResult.data) {
        setMembers(membersResult.data);
        loadMemberKPIProgress(membersResult.data);
      }

      if (documentsResult.data) {
        const docsByMember: Record<string, Document[]> = {};
        documentsResult.data.forEach((doc: any) => {
          if (!docsByMember[doc.squad_member_id]) docsByMember[doc.squad_member_id] = [];
          docsByMember[doc.squad_member_id].push(doc);
        });
        setDocuments(docsByMember);
        
        const commentIds = documentsResult.data.map((d: any) => d.id);
        if (commentIds.length > 0) {
          supabase.from('document_comments').select('*').in('document_id', commentIds).then(({ data }) => {
            if (data) {
              const commentsByDoc: Record<string, DocumentComment[]> = {};
              data.forEach((comment: any) => {
                if (!commentsByDoc[comment.document_id]) commentsByDoc[comment.document_id] = [];
                commentsByDoc[comment.document_id].push(comment);
              });
              setComments(commentsByDoc);
            }
          });
        }
      }

      if (tasksResult.data) {
        const tasksByMember: Record<string, Task[]> = {};
        tasksResult.data.forEach((task: any) => {
          if (!tasksByMember[task.squad_member_id]) tasksByMember[task.squad_member_id] = [];
          tasksByMember[task.squad_member_id].push(task);
        });
        setTasks(tasksByMember);
      }

      if (meetingsResult.data) {
        const meetingsByMember: Record<string, Meeting[]> = {};
        meetingsResult.data.forEach((meeting: any) => {
          if (!meetingsByMember[meeting.squad_member_id]) meetingsByMember[meeting.squad_member_id] = [];
          meetingsByMember[meeting.squad_member_id].push(meeting);
        });
        setMeetings(meetingsByMember);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast({ title: "Error", description: "Failed to load squad data.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const loadMemberKPIProgress = async (membersList: any[]) => {
    const kpiPromises = membersList.map(async (member) => {
      const [kpiDefs, completedKPIs] = await Promise.all([
        supabase.from('squad_kpi_definitions').select('id').eq('role', member.role),
        supabase.from('squad_member_kpis').select('status').eq('squad_member_id', member.id).in('status', ['approved', 'completed'])
      ]);
      return {
        memberId: member.id,
        completed: completedKPIs.data?.length || 0,
        total: kpiDefs.data?.length || 0
      };
    });
    
    const results = await Promise.all(kpiPromises);
    const progress: Record<string, { completed: number; total: number }> = {};
    results.forEach(r => {
      progress[r.memberId] = { completed: r.completed, total: r.total };
    });
    setMemberKPIProgress(progress);
  };

  const filteredDocuments = useMemo(() => {
    const docs = documents[activeDocumentMember?.id] || [];
    return docs.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(docFilters.search.toLowerCase()) ||
                           (doc.description || '').toLowerCase().includes(docFilters.search.toLowerCase());
      const matchesCategory = docFilters.category === 'all' || doc.category === docFilters.category;
      const matchesStatus = docFilters.status === 'all' || doc.status === docFilters.status;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [documents, activeDocumentMember, docFilters]);

  const syncData = async () => {
    setIsSyncing(true);
    await loadAllData();
    toast({ title: "Synced", description: "All data has been refreshed from the server." });
    setIsSyncing(false);
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.due_date || !selectedMember) {
      toast({ title: "Missing information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const { data, error } = await supabase
        .from('squad_tasks')
        .insert({
          squad_member_id: selectedMember.id,
          title: newTask.title,
          description: newTask.description,
          due_date: newTask.due_date,
          priority: newTask.priority,
          status: 'pending',
          created_by: squadUser?.memberId,
          created_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => ({
        ...prev,
        [selectedMember.id]: [...(prev[selectedMember.id] || []), data]
      }));

      setShowTaskDialog(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium' });
      toast({ title: "Task added", description: "New task has been created." });
    } catch (error) {
      console.error('Error adding task:', error);
      toast({ title: "Error", description: "Failed to add task.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleTask = async (memberId: string, taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      const { error } = await supabase
        .from('squad_tasks')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => ({
        ...prev,
        [memberId]: prev[memberId]?.map(task =>
          task.id === taskId ? { ...task, status: newStatus } : task
        ) || []
      }));

      toast({ title: newStatus === 'completed' ? "Task completed" : "Task reopened", description: newStatus === 'completed' ? "Great job!" : "Task moved back to pending." });
    } catch (error) {
      console.error('Error updating task:', error);
      toast({ title: "Error", description: "Failed to update task.", variant: "destructive" });
    }
  };

  const handleScheduleMeeting = async () => {
    if (!newMeeting.title || !newMeeting.meeting_date || !newMeeting.meeting_time || !selectedMember) {
      toast({ title: "Missing information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const { data: meetingData, error: meetingError } = await supabase
        .from('squad_meetings')
        .insert({
          squad_member_id: selectedMember.id,
          title: newMeeting.title,
          description: newMeeting.description,
          meeting_date: newMeeting.meeting_date,
          meeting_time: newMeeting.meeting_time,
          type: newMeeting.type,
          agenda: newMeeting.agenda,
          meeting_link: newMeeting.meeting_link,
          status: 'upcoming',
          created_by: squadUser?.memberId,
          created_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (meetingError) throw meetingError;

      const attendeeInserts = newMeeting.attendees.map(attendeeId => {
        const isAnalyst = attendeeId.startsWith('analyst');
        const attendeeInfo = isAnalyst 
          ? ANALYST_OPTIONS.find(a => a.id === attendeeId)
          : members.find(m => m.id === attendeeId);
        
        return {
          meeting_id: meetingData.id,
          attendee_id: attendeeId,
          attendee_name: attendeeInfo?.full_name || attendeeInfo?.name || attendeeId,
          attendee_role: attendeeInfo?.role || 'Team Member',
          attendee_type: isAnalyst ? 'analyst' : 'squad',
          rsvp_status: 'pending'
        };
      });

      if (attendeeInserts.length > 0) {
        const { error: attendeeError } = await supabase
          .from('meeting_attendees')
          .insert(attendeeInserts);

        if (attendeeError) throw attendeeError;
      }

      const newMeetingObj = {
        ...meetingData,
        attendees: attendeeInserts.map(a => ({ ...a, id: 'temp' }))
      };
      
      setMeetings(prev => ({
        ...prev,
        [selectedMember.id]: [...(prev[selectedMember.id] || []), newMeetingObj]
      }));

      setShowMeetingDialog(false);
      setNewMeeting({ title: '', description: '', meeting_date: '', meeting_time: '', type: 'internal', agenda: '', meeting_link: '', attendees: [] });
      toast({ title: "Meeting scheduled", description: "The meeting has been added to the calendar." });
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({ title: "Error", description: "Failed to schedule meeting.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleJoinMeeting = (meeting: Meeting) => {
    if (meeting.meeting_link) {
      window.open(meeting.meeting_link, '_blank');
      toast({ title: "Joining meeting", description: "Redirecting to meeting link..." });
    } else {
      toast({ title: "No meeting link", description: "This meeting doesn't have a link configured.", variant: "destructive" });
    }
  };

  const handleDocumentUpload = async () => {
    if (!newDocument.name || !newDocument.file || !activeDocumentMember) {
      toast({ title: "Missing information", description: "Please provide document name and file.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = newDocument.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `squad-documents/${activeDocumentMember.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, newDocument.file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      const { data: docData, error: docError } = await supabase
        .from('squad_documents')
        .insert({
          squad_member_id: activeDocumentMember.id,
          name: newDocument.name,
          description: newDocument.description,
          category: newDocument.category,
          file_url: urlData.publicUrl,
          file_name: newDocument.file.name,
          file_type: newDocument.file.type,
          file_size: `${(newDocument.file.size / 1024 / 1024).toFixed(1)} MB`,
          status: 'pending',
          uploaded_by: squadUser?.memberId,
          uploaded_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (docError) throw docError;

      setDocuments(prev => ({
        ...prev,
        [activeDocumentMember.id]: [...(prev[activeDocumentMember.id] || []), docData]
      }));

      setShowDocumentUploadDialog(false);
      setNewDocument({ name: '', description: '', category: 'other', file: null });
      toast({ title: "Document uploaded", description: `${newDocument.name} has been uploaded.` });
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({ title: "Error", description: "Failed to upload document.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: Document) => {
    try {
      if (doc.file_url) {
        window.open(doc.file_url, '_blank');
      } else {
        const content = `Document: ${doc.name}\nDescription: ${doc.description}\nCategory: ${doc.category}\nType: ${doc.file_type}\nSize: ${doc.file_size}\nUploaded By: ${doc.uploaded_by_name}\nUploaded At: ${new Date(doc.created_at).toLocaleDateString()}\nStatus: ${doc.status}`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.name}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast({ title: "Download started", description: `${doc.name} is being downloaded.` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download document.", variant: "destructive" });
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedDocument || !activeDocumentMember) return;

    setIsUploading(true);
    try {
      let attachmentUrl = null;
      
      if (commentAttachment) {
        const fileName = `comment-${Date.now()}-${commentAttachment.name}`;
        const filePath = `document-comments/${selectedDocument.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, commentAttachment);
        
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath);
          attachmentUrl = urlData.publicUrl;
        }
      }

      const { data: commentData, error: commentError } = await supabase
        .from('document_comments')
        .insert({
          document_id: selectedDocument.id,
          author_id: squadUser?.memberId,
          author_name: squadUser?.memberName,
          content: commentText
        })
        .select()
        .single();

      if (commentError) throw commentError;

      setComments(prev => ({
        ...prev,
        [selectedDocument.id]: [...(prev[selectedDocument.id] || []), commentData]
      }));

      setCommentText('');
      setCommentAttachment(null);
      setShowAddCommentDialog(false);
      
      toast({ title: "Comment added", description: "Your comment has been added to the document." });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({ title: "Error", description: "Failed to add comment.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateDocumentStatus = async (docId: string, newStatus: string) => {
    if (!activeDocumentMember) return;
    
    try {
      const { error } = await supabase
        .from('squad_documents')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', docId);

      if (error) throw error;

      setDocuments(prev => ({
        ...prev,
        [activeDocumentMember.id]: prev[activeDocumentMember.id]?.map(doc =>
          doc.id === docId ? { ...doc, status: newStatus } : doc
        ) || []
      }));

      if (selectedDocument && selectedDocument.id === docId) {
        setSelectedDocument({ ...selectedDocument, status: newStatus });
      }
      
      toast({ title: "Status updated", description: `Document status changed to ${newStatus}.` });
    } catch (error) {
      console.error('Error updating document status:', error);
      toast({ title: "Error", description: "Failed to update document status.", variant: "destructive" });
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!activeDocumentMember) return;
    
    try {
      const { error } = await supabase
        .from('squad_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      setDocuments(prev => ({
        ...prev,
        [activeDocumentMember.id]: prev[activeDocumentMember.id]?.filter(doc => doc.id !== docId) || []
      }));

      toast({ title: "Document deleted", description: "The document has been removed." });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: "Error", description: "Failed to delete document.", variant: "destructive" });
    }
  };

  const handleSendMessage = () => {
    if (!messageContent.trim() || !selectedMember) {
      toast({ title: "Message is empty", description: "Please enter a message to send.", variant: "destructive" });
      return;
    }
    
    if (selectedMember.id === squadUser?.memberId) {
      toast({ title: "Cannot message self", description: "You cannot send a message to yourself.", variant: "destructive" });
      setShowMessageDialog(false);
      return;
    }
    
    toast({ title: "Message sent", description: `Your message to ${selectedMember.full_name} has been sent.` });
    setShowMessageDialog(false);
    setMessageContent('');
  };

  const handleSaveProfile = async () => {
    if (!selectedMember || selectedMember.id !== squadUser?.memberId) {
      toast({ title: "Permission denied", description: "You can only edit your own profile.", variant: "destructive" });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('squad_members')
        .update({
          bio: editedBio,
          phone: editedPhone,
          location: editedLocation,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedMember.id);

      if (error) throw error;

      setMembers(prev => prev.map(m =>
        m.id === selectedMember.id ? { ...m, bio: editedBio, phone: editedPhone, location: editedLocation } : m
      ));

      setShowEditProfileDialog(false);
      toast({ title: "Profile updated", description: "Your profile has been saved." });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({ title: "Error", description: "Failed to update profile.", variant: "destructive" });
    }
  };

  const openMemberActions = (member: any, action: string) => {
    setSelectedMember(member);
    setEditedBio(member.bio || '');
    setEditedPhone(member.phone || '');
    setEditedLocation(member.location || '');
    
    switch(action) {
      case 'task': setShowTaskDialog(true); break;
      case 'meeting': setShowMeetingDialog(true); break;
      case 'message': setShowMessageDialog(true); break;
      case 'edit': setShowEditProfileDialog(true); break;
      case 'documents': 
        setActiveDocumentMember(member);
        setShowDocumentsTabDialog(true);
        break;
    }
  };

  const openDocumentComments = (doc: Document, member: any) => {
    setSelectedDocument(doc);
    setActiveDocumentMember(member);
    setShowDocumentCommentsDialog(true);
  };

  const totalKPIsAll = Object.values(memberKPIProgress).reduce((sum, m) => sum + m.total, 0);
  const completedKPIsAll = Object.values(memberKPIProgress).reduce((sum, m) => sum + m.completed, 0);
  const isOwnProfile = (member: any) => member.id === squadUser?.memberId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 pb-20">
        {/* Sticky Header */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 -mx-4 px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/forge/dashboard')}
                className="md:hidden"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold">Squad Members</h1>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {members.length} team members
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={syncData} disabled={isSyncing}>
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline ml-2">{isSyncing ? 'Syncing...' : 'Sync'}</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xl md:text-2xl font-bold">{members.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">Members</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold">{completedKPIsAll}/{totalKPIsAll}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">KPIs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold">
                {totalKPIsAll > 0 ? Math.round((completedKPIsAll / totalKPIsAll) * 100) : 0}%
              </p>
              <p className="text-[10px] md:text-xs text-muted-foreground">Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold">{members.filter(m => m.assessment_completed).length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">Assessments</p>
            </CardContent>
          </Card>
          <Card className="col-span-2 md:col-span-1">
            <CardContent className="p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold">
                {Object.values(tasks).flat().filter(t => t.status === 'completed').length}
              </p>
              <p className="text-[10px] md:text-xs text-muted-foreground">Tasks Done</p>
            </CardContent>
          </Card>
        </div>

        {/* Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {members.map((member) => {
            const progress = memberKPIProgress[member.id] || { completed: 0, total: 0 };
            const percentComplete = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
            const memberTasks = tasks[member.id] || [];
            const pendingTasks = memberTasks.filter((t: Task) => t.status !== 'completed').length;
            const memberMeetings = meetings[member.id] || [];
            const upcomingMeeting = memberMeetings.find(m => m.status === 'upcoming');
            const isSelf = isOwnProfile(member);
            
            return (
              <Card key={member.id} className="overflow-hidden hover:shadow-lg transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 md:h-12 md:w-12 rounded-full flex items-center justify-center text-base md:text-lg font-bold ${getRoleBadgeColor(member.role)}`}>
                        {getInitials(member.full_name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm md:text-base">{member.full_name}</h3>
                        <Badge className={getRoleBadgeColor(member.role)}>
                          {getDisplayRole(member.role)}
                        </Badge>
                        {isSelf && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openMemberActions(member, 'task')}>
                          <Plus className="h-4 w-4 mr-2" /> Add Task
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMemberActions(member, 'meeting')}>
                          <CalendarPlus className="h-4 w-4 mr-2" /> Schedule Meeting
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMemberActions(member, 'documents')}>
                          <FolderOpen className="h-4 w-4 mr-2" /> Documents ({(documents[member.id] || []).length})
                        </DropdownMenuItem>
                        {!isSelf && (
                          <DropdownMenuItem onClick={() => openMemberActions(member, 'message')}>
                            <MessageSquare className="h-4 w-4 mr-2" /> Send Message
                          </DropdownMenuItem>
                        )}
                        {isSelf && (
                          <DropdownMenuItem onClick={() => openMemberActions(member, 'edit')}>
                            <Edit className="h-4 w-4 mr-2" /> Edit Profile
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => navigate(`/forge/squad/${member.id}`)}>
                          <Eye className="h-4 w-4 mr-2" /> Full Profile
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      <span className="truncate text-xs">{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        <span className="text-xs">{member.phone}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground">KPI Progress</span>
                      <span>{progress.completed}/{progress.total}</span>
                    </div>
                    <Progress value={percentComplete} className="h-1" />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span className="text-[10px]">Tasks: {pendingTasks}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {member.assessment_completed ? '✓ Complete' : '⏳ Pending'}
                    </Badge>
                  </div>

                  {upcomingMeeting && (
                    <div className="pt-1 border-t">
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span className="truncate">Next: {upcomingMeeting.title}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {members.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No squad members found</p>
          </div>
        )}
      </div>

      {/* ========== DIALOGS WITH PROPER SCROLLING ========== */}

      {/* Documents Tab Dialog */}
      <Dialog open={showDocumentsTabDialog} onOpenChange={setShowDocumentsTabDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 md:px-6 pt-4 md:pt-6 pb-2 border-b">
            <DialogTitle>Documents - {activeDocumentMember?.full_name || 'Member'}</DialogTitle>
            <DialogDescription>Manage documents, add comments, and track status</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-4">
            <div className="sticky top-0 z-10 bg-background pt-0 pb-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={docFilters.search}
                    onChange={(e) => setDocFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-9"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={docFilters.category} onValueChange={(value) => setDocFilters(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="proposal">Proposal</SelectItem>
                      <SelectItem value="research">Research</SelectItem>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={docFilters.status} onValueChange={(value) => setDocFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewing">Reviewing</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-between items-center mt-3">
                <p className="text-xs text-muted-foreground">{filteredDocuments.length} documents found</p>
                <Button size="sm" onClick={() => setShowDocumentUploadDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Upload
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No documents match your filters</p>
                </div>
              ) : (
                filteredDocuments.map((doc: Document) => (
                  <Card key={doc.id} className="p-3 md:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-primary shrink-0" />
                          <div>
                            <h4 className="font-semibold text-sm md:text-base">{doc.name}</h4>
                            <p className="text-xs text-muted-foreground line-clamp-2">{doc.description || 'No description'}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">{doc.category}</Badge>
                          <span className="text-[10px] text-muted-foreground">{doc.file_type}</span>
                          <span className="text-[10px] text-muted-foreground">{doc.file_size}</span>
                          <span className="text-[10px] text-muted-foreground">By {doc.uploaded_by_name}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          {getStatusBadge(doc.status)}
                          <span className="text-[10px] text-muted-foreground">
                            {(comments[doc.id] || []).length} comments
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openDocumentComments(doc, activeDocumentMember)}>
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        {isOwnProfile(activeDocumentMember) && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
          
          <DialogFooter className="px-4 md:px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowDocumentsTabDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Comments Dialog */}
      <Dialog open={showDocumentCommentsDialog} onOpenChange={setShowDocumentCommentsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 border-b">
            <DialogTitle>{selectedDocument?.name || 'Document'} - Comments</DialogTitle>
            <DialogDescription>{selectedDocument?.description || 'No description'}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b">
              {getStatusBadge(selectedDocument?.status || 'pending')}
              {activeDocumentMember && isOwnProfile(activeDocumentMember) && selectedDocument && (
                <Select onValueChange={(value) => handleUpdateDocumentStatus(selectedDocument.id, value)}>
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue placeholder="Change status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="space-y-4">
              {(comments[selectedDocument?.id || ''] || []).map((comment) => (
                <div key={comment.id} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-sm">{comment.author_name}</span>
                    <span className="text-xs text-muted-foreground">{new Date(comment.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm mt-1">{comment.content}</p>
                </div>
              ))}
              
              {(comments[selectedDocument?.id || ''] || []).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No comments yet</p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="px-6 py-4 border-t flex justify-between">
            <Button variant="outline" onClick={() => setShowAddCommentDialog(true)}>
              <MessageCircle className="h-4 w-4 mr-2" /> Add Comment
            </Button>
            <Button variant="outline" onClick={() => setShowDocumentCommentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={showAddCommentDialog} onOpenChange={setShowAddCommentDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Add Comment to {selectedDocument?.name || 'Document'}</DialogTitle>
            <DialogDescription>Share your feedback or ask questions about this document</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <Textarea
              placeholder="Type your comment here..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={4}
            />
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm">Attach file (optional)</p>
              <input type="file" ref={commentFileInputRef} onChange={(e) => setCommentAttachment(e.target.files?.[0] || null)} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => commentFileInputRef.current?.click()}>
                {commentAttachment ? commentAttachment.name : 'Choose File'}
              </Button>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowAddCommentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddComment} disabled={!commentText.trim() || isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Post Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showDocumentUploadDialog} onOpenChange={setShowDocumentUploadDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Upload Document for {activeDocumentMember?.full_name || 'Member'}</DialogTitle>
            <DialogDescription>Add document details and upload file</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="docName">Document Title *</Label>
              <Input
                id="docName"
                placeholder="e.g., Q1 Market Research Report"
                value={newDocument.name}
                onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="docDescription">Description</Label>
              <Textarea
                id="docDescription"
                placeholder="What is this document about?"
                value={newDocument.description}
                onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="docCategory">Category</Label>
              <Select value={newDocument.category} onValueChange={(value) => setNewDocument({...newDocument, category: value as any})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="research">Research</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {newDocument.file ? newDocument.file.name : 'Click to select file'}
                </p>
                <input type="file" className="hidden" id="doc-upload" ref={fileInputRef} onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setNewDocument({...newDocument, file: e.target.files[0]});
                  }
                }} />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Browse Files
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowDocumentUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleDocumentUpload} disabled={!newDocument.file || !newDocument.name || isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Add Task for {selectedMember?.full_name || 'Member'}</DialogTitle>
            <DialogDescription>Create a new task assignment</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="taskTitle">Task Title *</Label>
              <Input
                id="taskTitle"
                placeholder="e.g., Complete market research"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taskDescription">Description</Label>
              <Textarea
                id="taskDescription"
                placeholder="Additional details about this task"
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date *</Label>
              <Input
                id="dueDate"
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={newTask.priority} onValueChange={(value) => setNewTask({...newTask, priority: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTask} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Meeting Dialog */}
      <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Schedule Meeting with {selectedMember?.full_name || 'Member'}</DialogTitle>
            <DialogDescription>Schedule a meeting with squad members and analysts</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="meetingTitle">Meeting Title *</Label>
              <Input
                id="meetingTitle"
                placeholder="e.g., Weekly Squad Sync"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meetingDescription">Description</Label>
              <Textarea
                id="meetingDescription"
                placeholder="What is this meeting about?"
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({...newMeeting, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={newMeeting.meeting_date}
                  onChange={(e) => setNewMeeting({...newMeeting, meeting_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  type="time"
                  value={newMeeting.meeting_time}
                  onChange={(e) => setNewMeeting({...newMeeting, meeting_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="meetingType">Meeting Type</Label>
              <Select value={newMeeting.type} onValueChange={(value) => setNewMeeting({...newMeeting, type: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal (Squad)</SelectItem>
                  <SelectItem value="analyst">Analyst Review</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Invite Attendees</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Squad Members</p>
                {members.map((member) => (
                  <div key={member.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`attendee-${member.id}`}
                      checked={newMeeting.attendees.includes(member.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewMeeting({...newMeeting, attendees: [...newMeeting.attendees, member.id]});
                        } else {
                          setNewMeeting({...newMeeting, attendees: newMeeting.attendees.filter(id => id !== member.id)});
                        }
                      }}
                    />
                    <Label htmlFor={`attendee-${member.id}`} className="text-sm cursor-pointer">
                      {member.full_name} ({getDisplayRole(member.role)})
                    </Label>
                  </div>
                ))}
                <p className="text-xs font-semibold text-muted-foreground mt-3 mb-2">Analysts</p>
                {ANALYST_OPTIONS.map((analyst) => (
                  <div key={analyst.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`attendee-${analyst.id}`}
                      checked={newMeeting.attendees.includes(analyst.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewMeeting({...newMeeting, attendees: [...newMeeting.attendees, analyst.id]});
                        } else {
                          setNewMeeting({...newMeeting, attendees: newMeeting.attendees.filter(id => id !== analyst.id)});
                        }
                      }}
                    />
                    <Label htmlFor={`attendee-${analyst.id}`} className="text-sm cursor-pointer">
                      {analyst.name} ({analyst.role})
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agenda">Agenda</Label>
              <Textarea
                id="agenda"
                placeholder="What will be covered in this meeting?"
                value={newMeeting.agenda}
                onChange={(e) => setNewMeeting({...newMeeting, agenda: e.target.value})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meetingLink">Meeting Link (Optional)</Label>
              <Input
                id="meetingLink"
                placeholder="https://meet.google.com/..."
                value={newMeeting.meeting_link}
                onChange={(e) => setNewMeeting({...newMeeting, meeting_link: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowMeetingDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleMeeting} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarPlus className="h-4 w-4 mr-2" />}
              Schedule Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Send Message to {selectedMember?.full_name || 'Member'}</DialogTitle>
            <DialogDescription>Send a direct message to this team member</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Textarea
              placeholder="Type your message here..."
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage}><Send className="h-4 w-4 mr-2" /> Send Message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfileDialog} onOpenChange={setShowEditProfileDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Edit Your Profile</DialogTitle>
            <DialogDescription>Update your personal information</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editBio">Bio</Label>
              <Textarea
                id="editBio"
                value={editedBio}
                onChange={(e) => setEditedBio(e.target.value)}
                rows={3}
                placeholder="Tell us about yourself..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editPhone">Phone</Label>
                <Input
                  id="editPhone"
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLocation">Location</Label>
                <Input
                  id="editLocation"
                  value={editedLocation}
                  onChange={(e) => setEditedLocation(e.target.value)}
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setShowEditProfileDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile}><Save className="h-4 w-4 mr-2" /> Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}